import { createRouter, createWebHistory, type LocationQuery, type RouteRecordRaw } from "vue-router";
import { firstAuthorizedMenuPath } from "../layout/menu";
import { useAuthStore } from "../stores/auth";
import { workspaceRedirect, workspaceRoute } from "../ui/workspace";
import { customersWorkspace, organizationsWorkspace, salesDocumentsWorkspace, documentTemplatesWorkspace } from "../../modules/customers/crmWorkspaces";
import { executionWorkspace, externalWorkspace } from "../../modules/execution/executionWorkspaces";
import { procurementWorkspace, warehouseWorkspace } from "../../modules/procurement/procurementWorkspaces";
import { auditWorkspace, notificationSettingsWorkspace } from "../../modules/operations/systemWorkspaces";
import { administrationWorkspace, attendanceWorkspace, payrollWorkspace, requestsWorkspace } from "../../modules/workforce/workforceWorkspaces";
import { accountingCatalogsWorkspace, cashBankWorkspace, financeWorkspace, invoicesWorkspace, vouchersWorkspace } from "../../modules/finance/accountingWorkspaces";
import { workInboxWorkspace } from "../../modules/workflow/workInboxWorkspace";
import { reportsLegacyLocation } from "../../modules/reports/reportsWorkspace";

/** Link cũ của màn tiếp nhận (HCNS/Kế toán) → tab "Hồ sơ tiếp nhận", giữ query (vd. ?receiptId=) và lọc sẵn bộ phận. */
function receiptsLocation(query: Record<string, unknown>, receiptType: "admin" | "accounting") {
  return { path: "/requests", query: { ...query, view: "receipts", receiptType } as Record<string, string> };
}
const ANALYTICS_VIEWS: Record<string, string> = { projects: "projects", finance: "finance", resources: "resources", issues: "quality", approvals: "people", procurement: "procurement", quality: "quality", people: "people" };
// ADR-109 (điều chỉnh 2): /execution là workspace Thực thi (my-work / warnings / reports). Tab cũ của «Thực thi» lát 2 và các route riêng
// của lát 3–4 (/projects/my-work, /projects/tracking, /reports) chuyển hướng về đây, giữ các query khác (vd ?project=, ?reportId=).
// «Bảng tiến độ» (monitoring/board) và «Cập nhật theo dự án» (progress) đã bỏ → chi tiết dự án › WBS & Gantt (projectExecutionLocation).
const EXECUTION_LEGACY_VIEWS: Record<string, [path: string, view: string]> = {
  "progress-warnings": ["/execution", "warnings"], "external-customer": ["/projects/external", "customer"], "external-subcontractor": ["/projects/external", "subcontractor"],
};
/** «Tổ của tôi» cũ (/team-hub, /execution?view=team, /attendance?view=team) → Hộp phê duyệt › Chấm công › góc nhìn «Theo tổ». */
function teamViewLocation(query: LocationQuery) { const { view: _view, ...rest } = query; return { path: "/work", query: { ...rest, view: "approvals", team: "1" } }; }
/** Chi tiết dự án › tab WBS & Gantt (ProjectRegistrationView đổi ?project=&tab= thành panel); không có dự án thì mở sổ dự án. */
function projectExecutionLocation(query: LocationQuery) {
  const { view: _view, project, ...rest } = query;
  return { path: "/project-registration", query: typeof project === "string" && project ? { ...rest, project, tab: "execution" } : rest };
}
/** «Tổng quan» của Theo dõi cũ → thẻ «Theo dõi tiến độ» của Hộp công việc (ADR-109 điều chỉnh 27/10: quản lý kiểm tra hằng ngày; cùng màn với widget Dashboard). */
function progressOverviewLocation(query: LocationQuery) { const { view: _view, ...rest } = query; return { path: "/work", query: { ...rest, view: "progress" } }; }
function executionLegacyRedirect(query: LocationQuery) {
  const { view, ...rest } = query;
  if (view === "team") return teamViewLocation(query);
  if (view === "monitoring" || view === "progress" || view === "board") return projectExecutionLocation(query);
  if (view === "overview") return progressOverviewLocation(query);
  const legacy = EXECUTION_LEGACY_VIEWS[String(view ?? "")];
  if (legacy) return { path: legacy[0], query: { ...rest, view: legacy[1] } };
  return reportsLegacyLocation(query) ?? true;
}
/** «Theo dõi» của lát 3–4 (/projects/tracking): ?view=board → chi tiết dự án, ?view=overview → Hộp công việc › Theo dõi tiến độ, còn lại → tab Cảnh báo. */
function trackingLegacyRedirect(query: LocationQuery) {
  if (query.view === "board") return projectExecutionLocation(query);
  if (query.view === "overview") return progressOverviewLocation(query);
  const { view: _view, ...rest } = query;
  return { path: "/execution", query: { ...rest, view: "warnings" } };
}
/** Chấm công & lịch: hai tab cũ «Báo cáo công việc hàng ngày» và «Tổ của tôi» đã chuyển đi (ADR-108). */
function attendanceLegacyRedirect(query: LocationQuery) {
  if (query.view === "admin-receipts") return receiptsLocation(query, "admin");
  if (query.view === "team") return teamViewLocation(query);
  if (query.view === "timesheets") { const { view: _view, ...rest } = query; return { path: "/execution", query: { ...rest, view: "my-work" } }; }
  return true;
}

export function hasRoutePermission(
  permission: string | string[] | undefined,
  permissionMode: "any" | "all" | undefined,
  can: (permission: string) => boolean,
) {
  const required =
    typeof permission === "string" ? [permission] : (permission ?? []);
  if (!required.length) return true;
  return permissionMode === "all" ? required.every(can) : required.some(can);
}

function overviewRedirect(path: string, name: string, view: string, fallback: string): RouteRecordRaw {
  return { path, name, redirect: () => (useAuthStore().can("ProjectDashboards.Read") ? { path: "/", query: { view } } : fallback) };
}

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: "/forgot-password", name: "forgot-password", component: () => import("../../modules/auth/PasswordRecoveryView.vue"), meta: { public: true } },
    { path: "/reset-password", name: "reset-password", component: () => import("../../modules/auth/PasswordRecoveryView.vue"), meta: { public: true } },
    // Trang pháp lý công khai cho màn hình xin quyền OAuth (Google/Microsoft) và trang đăng nhập.
    { path: "/privacy", name: "privacy", component: () => import("../../modules/legal/LegalPage.vue"), props: { kind: "privacy" }, meta: { public: true, legal: true } },
    { path: "/terms", name: "terms", component: () => import("../../modules/legal/LegalPage.vue"), props: { kind: "terms" }, meta: { public: true, legal: true } },
    // ADR-085: đăng nhập một lần cho ứng dụng hệ sinh thái Q One ("Đăng nhập bằng Q One").
    { path: "/sso", name: "sso", component: () => import("../../modules/auth/SsoContinueView.vue"), meta: { public: true } },
    // BigUpdate D15 — "Đăng nhập bằng Q One" (Q One Platform OIDC): nơi Platform trả code về.
    { path: "/auth/callback", name: "platform-login-callback", component: () => import("../../modules/auth/PlatformLoginCallbackView.vue"), meta: { public: true } },
    {
      path: "/login",
      name: "login",
      component: () => import("../../modules/auth/LoginView.vue"),
      meta: { public: true },
    },
    {
      path: "/",
      component: () => import("../layout/AppLayout.vue"),
      children: [
        // ADR-109 (điều chỉnh 27/10): Hộp công việc giữ thẻ «Theo dõi tiến độ» (Gantt 3 năm) — cùng màn với widget «Tổng quan tiến độ» của Dashboard.
        workspaceRoute("work", "work", ["menu.overview", "nav.workInbox"], workInboxWorkspace),
        workspaceRedirect("today", "today", "/work", "today"),
        workspaceRedirect("approval-tasks", "approval-tasks", "/work", "approvals"),
        { path: "my-follow-ups/:id", name: "my-follow-up", component: () => import("../../modules/customers/OwnFollowUpView.vue"), meta: { permission: ["Customers.Read", "CustomerInteractions.Read"], permissionMode: "all", breadcrumb: ["ownFollowUp.title"] } },
        {
          path: "",
          name: "dashboard",
          component: () => import("../../modules/dashboard/DashboardView.vue"),
          meta: {
            permission: "ProjectDashboards.Read",
            breadcrumb: ["nav.dashboard"],
          },
        },
        // Phân tích điều hành đã gộp vào Dashboard: tab cũ → view tương ứng (bookmark/link cũ vẫn mở đúng chỗ).
        { path: "analytics", name: "analytics", redirect: to => ({ path: "/", query: { view: ANALYTICS_VIEWS[String(to.query.tab ?? "")] ?? "projects" } }) },
        {
          path: "account",
          name: "account",
          component: () =>
            import("../../modules/auth/AccountSettingsView.vue"),
          meta: {
            breadcrumb: ["nav.accountSettings"],
          },
        },
        // docs/upgrade/06 (M3): CRM, Dự án, Mua hàng gom thành workspace nhiều tab; route cũ redirect sang `?view=`.
        workspaceRoute("customers", "customers", ["menu.crm", "nav.customers"], customersWorkspace),
        workspaceRoute("organizations", "organizations", ["menu.crm", "menu.organizations"], organizationsWorkspace),
        workspaceRoute("sales-documents", "sales-documents", ["menu.crm", "menu.salesDocuments"], salesDocumentsWorkspace),
        workspaceRoute("document-templates", "document-templates", ["menu.crm", "menu.documentTemplates"], documentTemplatesWorkspace),
        // ADR-109 (điều chỉnh 2): nhóm Dự án 3 mục — Thực thi (Của tôi / Cảnh báo / Báo cáo dự án), Dự án, Chia sẻ ngoài.
        { ...workspaceRoute("execution", "execution", ["menu.projectManagement", "menu.projectExecution"], executionWorkspace), beforeEnter: to => executionLegacyRedirect(to.query) },
        workspaceRedirect("projects/my-work", "project-my-work", "/execution", "my-work"),
        { path: "projects/tracking", name: "project-tracking", redirect: to => trackingLegacyRedirect(to.query) },
        workspaceRoute("projects/external", "project-external", ["menu.projectManagement", "menu.projectExternal"], externalWorkspace),
        workspaceRoute("procurement", "procurement", ["menu.purchasing", "menu.procurementDocuments"], procurementWorkspace),
        workspaceRoute("warehouse", "warehouse", ["menu.purchasing", "menu.warehouse"], warehouseWorkspace),
        workspaceRedirect("partners", "partners", "/organizations", "partners"),
        workspaceRedirect("suppliers", "suppliers", "/organizations", "suppliers"),
        {
          path: "products",
          name: "products",
          component: () => import("../../modules/products/ProductsView.vue"),
          meta: {
            permission: "Products.Read",
            breadcrumb: ["menu.crm", "nav.products"],
          },
        },
        workspaceRedirect("exchange-rates", "exchange-rates", "/accounting-catalogs", "rates"),
        workspaceRedirect("workforce/annual-leave", "annual-leave", "/requests", "annual-leave"),
        workspaceRedirect("customer-interactions", "customer-interactions", "/customers", "interactions"),
        workspaceRedirect("material-inventory", "material-inventory", "/warehouse", "inventory"),
        {
          path: "materials",
          name: "materials",
          component: () => import("../../modules/materials/MaterialsView.vue"),
          meta: {
            permission: "Materials.Read",
            breadcrumb: ["menu.purchasing", "nav.materials"],
          },
        },
        {
          path: "crm-trash",
          name: "crm-trash",
          component: () =>
            import("../../modules/masterdata/MasterDataTrashView.vue"),
          meta: {
            permission: [
              "Customers.Manage",
              "Partners.Manage",
              "Suppliers.Manage",
              "Materials.Manage",
              "Products.Manage",
            ],
            permissionMode: "any",
            breadcrumb: ["menu.system", "crmTrash.title"],
          },
        },
        {
          path: "project-registration",
          name: "project-registration",
          component: () =>
            import("../../modules/projects/ProjectRegistrationView.vue"),
          meta: {
            permission: "Projects.Read",
            breadcrumb: ["menu.projectManagement", "nav.projectRegistration"],
          },
        },
        // ADR-109: «Bảng tiến độ» và «Cập nhật theo dự án» → chi tiết dự án › WBS & Gantt (giữ ?project=).
        { path: "monitoring", name: "monitoring", redirect: to => projectExecutionLocation(to.query) },
        { path: "progress-updates", name: "progress-updates", redirect: to => projectExecutionLocation(to.query) },
        workspaceRedirect("external-progress/customer", "external-customer-progress", "/projects/external", "customer"),
        workspaceRedirect("external-progress/subcontractor", "external-subcontractor-progress", "/projects/external", "subcontractor"),
        workspaceRedirect("my-work", "my-work", "/execution", "my-work"),
        { path: "team-hub", name: "team-hub", redirect: to => teamViewLocation(to.query) },
        // ADR-108: trang «Dự án đang thực hiện» bỏ khỏi điều hướng; chi tiết dự án (/projects/active/:id) giữ cho liên kết cũ.
        { path: "projects/active", name: "active-projects", redirect: "/project-registration" },
        { path: "projects/active/:id", name:"execution-project-detail", component:()=>import("../../modules/projects/ProjectDetailView.vue"), meta:{permission:["Projects.Read","Chat.Read"],permissionMode:"any",breadcrumb:["menu.projectManagement","executionHub.activeProjects"]} },
        { path: "projects", name: "projects", redirect: "/execution" },
        {
          path: "projects/:id",
          redirect: (route) => ({
            path: "/project-registration",
            query: { project: String(route.params.id) },
          }),
        },
        workspaceRedirect("project-contracts", "project-contracts", "/sales-documents", "contracts"),
        workspaceRedirect("quotations", "quotations", "/sales-documents", "quotations"),
        workspaceRedirect("purchase-requests", "purchase-requests", "/procurement", "requests"),
        workspaceRedirect("purchase-orders", "purchase-orders", "/procurement", "orders"),
        workspaceRedirect("timesheets", "timesheets", "/execution", "my-work"),
        // ADR-109: Báo cáo dự án là tab «reports» của Thực thi; tab cũ (issues/lessons/schedules) và link email cũ (?reportId=, ?scheduleId=) đổi theo.
        { path: "reports", name: "reports", redirect: to => reportsLegacyLocation(to.query) ?? { path: "/execution", query: { ...to.query, view: "reports" } } },
        {
          path: "notifications/:id?",
          name: "notifications",
          component: () =>
            import("../../modules/notifications/NotificationsView.vue"),
          meta: {
            permission: "Notifications.Read",
            breadcrumb: ["menu.overview", "nav.notifications"],
          },
        },
        workspaceRedirect("notification-rules", "notification-rules", "/notification-settings", "rules"),
        {
          path: "system-settings",
          name: "system-settings",
          component: () => import("../../modules/identity/SystemSettingsView.vue"),
          meta: { permission: "SystemSettings.Read", breadcrumb: ["menu.system", "systemSettings.title"] },
        },
        // docs/upgrade/06 (M4): Thông báo và Nhật ký & chẩn đoán là workspace nhiều tab.
        workspaceRoute("notification-settings", "notification-settings", ["menu.system", "menu.notificationSettings"], notificationSettingsWorkspace),
        // ADR-078 + 13/10/2026: Chẩn đoán hệ thống gộp vào Audit › Nhật ký & chẩn đoán; link cũ /diagnostics(?view=audit|outbox) vào đúng phần.
        { path: "diagnostics", name: "diagnostics", redirect: (to) => ({ path: "/audit", query: { ...to.query, view: "log", section: to.query.view === "audit" ? "trail" : "outbox" } }) },
        workspaceRoute("audit", "audit", ["menu.system", "menu.audit"], auditWorkspace),
        workspaceRedirect("audit-trail", "audit-trail", "/audit", "log"),
        { path: "operations/outbox", name: "outbox-diagnostics", redirect: (to) => ({ path: "/audit", query: { section: "outbox", ...to.query, view: "log" } }) },
        workspaceRedirect("notification-occurrences", "notification-occurrences", "/notification-settings", "occurrences"),
        workspaceRedirect("work-calendar", "work-calendar", "/attendance", "calendar"),
        workspaceRedirect("manday-cost", "manday-cost", "/payroll", "manday-cost"),
        // docs/upgrade/06 (M2): Nhân sự gom thành workspace nhiều tab; route cũ redirect sang `?view=`.
        workspaceRoute("payroll", "payroll", ["menu.hrAdministration", "menu.payrollGroup"], payrollWorkspace),
        {
          ...workspaceRoute("requests", "requests", ["menu.hrAdministration", "menu.requestsGroup"], requestsWorkspace),
          // Link cũ /requests?view=admin-receipts: tab HCNS tiếp nhận đã gộp vào "Hồ sơ tiếp nhận" (lọc sẵn bộ phận Nhân sự).
          beforeEnter: to => (to.query.view === "admin-receipts" ? receiptsLocation(to.query, "admin") : true),
        },
        {
          ...workspaceRoute("attendance", "attendance", ["menu.hrAdministration", "menu.attendanceGroup"], attendanceWorkspace),
          // Link cũ /attendance?view=admin-receipts: sang "Hồ sơ tiếp nhận" của Đơn từ & Tiếp nhận; ?view=timesheets|team sang nơi mới (ADR-108).
          beforeEnter: to => attendanceLegacyRedirect(to.query),
        },
        workspaceRoute("administration", "administration", ["menu.hrAdministration", "menu.administration"], administrationWorkspace),
        workspaceRedirect("advance-requests", "advance-requests", "/vouchers", "advance"),
        workspaceRedirect("payment-requests", "payment-requests", "/vouchers", "payment"),
        workspaceRedirect("advance-settlements", "advance-settlements", "/vouchers", "settlement"),
        // docs/upgrade/06 (M1): Kế toán gom thành 5 workspace nhiều tab; route cũ ở trên redirect sang `?view=`.
        workspaceRoute("vouchers", "vouchers", ["menu.accounting", "menu.paymentVouchers"], vouchersWorkspace),
        workspaceRoute("invoices", "invoices", ["menu.accounting", "menu.invoices"], invoicesWorkspace),
        workspaceRoute("cash-bank", "cash-bank", ["menu.accounting", "menu.cashAndBank"], cashBankWorkspace),
        workspaceRoute("finance", "finance", ["menu.accounting", "menu.financeReports"], financeWorkspace),
        workspaceRoute("accounting-catalogs", "accounting-catalogs", ["menu.accounting", "menu.accountingCatalogs"], accountingCatalogsWorkspace),
        {
          path: "payment-requests/:id/print",
          name: "payment-request-print",
          component: () => import("../../modules/payments/PaymentRequestPrintView.vue"),
          meta: { permission: "PaymentRequests.Read", breadcrumb: ["menu.accounting", "nav.paymentRequests"] },
        },
        workspaceRedirect("expense-categories", "expense-categories", "/accounting-catalogs", "expense"),
        {
          path: "project-finance/:id?",
          name: "project-finance",
          component: () => import("../../modules/projects/ProjectFinanceView.vue"),
          meta: { permission: "Projects.Read", breadcrumb: ["menu.accounting", "nav.projectFinance"] },
        },
        workspaceRedirect("cash-book", "cash-book", "/cash-bank", "cash"),
        workspaceRedirect("finance-statement", "finance-statement", "/finance", "statement"),
        workspaceRedirect("finance-period-close", "finance-period-close", "/finance", "period-close"),
        workspaceRedirect("bank-statements", "bank-statements", "/cash-bank", "bank"),
        workspaceRedirect("amortizations", "amortizations", "/finance", "amortizations"),
        workspaceRedirect("supplier-invoices", "supplier-invoices", "/invoices", "supplier"),
        workspaceRedirect("my-payslips", "my-payslips", "/payroll", "mine"),
        workspaceRedirect("attendance-administration", "attendance-administration", "/attendance", "administration"),
        workspaceRedirect("attendance-corrections", "attendance-corrections", "/requests", "attendance-correction"),
        workspaceRedirect("leave-requests", "leave-requests", "/requests", "leave"),
        workspaceRedirect("overtime-requests", "overtime-requests", "/requests", "overtime"),
        {
          path: "onsite-requests",
          name: "onsite-requests",
          component: () =>
            import("../../modules/workforce/OperationalLedgerView.vue"),
          props: { requestType: "Onsite" },
          meta: {
            breadcrumb: ["menu.hrAdministration", "nav.onsiteRequests"],
          },
        },
        workspaceRedirect("business-trips", "business-trips", "/requests", "business-trip"),
        workspaceRedirect("vehicle-requests", "vehicle-requests", "/requests", "vehicle"),
        workspaceRedirect("company-vehicles", "company-vehicles", "/administration", "vehicles"),
        workspaceRedirect("meal-registrations", "meal-registrations", "/administration", "meals"),
        workspaceRedirect("material-registrations", "material-registrations", "/warehouse", "registrations"),
        // Tiếp nhận của HCNS và Kế toán gộp vào /requests?view=receipts; link cũ giữ query và lọc sẵn bộ phận.
        { path: "admin-receipts", name: "admin-receipts", redirect: to => receiptsLocation(to.query, "admin") },
        { path: "accounting-receipts", name: "accounting-receipts", redirect: to => receiptsLocation(to.query, "accounting") },
        // Nhân sự › Hồ sơ nhân viên: màn riêng chỉ có hồ sơ (không có phân quyền); phân quyền nằm ở Hệ thống › /identity-administration.
        {
          path: "employee-profiles",
          name: "employee-profiles",
          component: () =>
            import("../../modules/identity/IdentityAdministrationView.vue"),
          props: { area: "people" },
          meta: {
            permission: "EmployeeProfiles.Read",
            breadcrumb: ["menu.hrAdministration", "nav.employeeProfiles"],
          },
        },
        {
          path: "skill-map",
          name: "skill-map",
          component: () => import("../../modules/workforce/SkillMapView.vue"),
          meta: {
            permission: ["SkillAssessments.Read"],
            permissionMode: "any",
            breadcrumb: ["menu.hrAdministration", "nav.skillMap"],
          },
        },
        workspaceRedirect("controlled-documents", "controlled-documents", "/administration", "documents"),
        workspaceRedirect("finance-report-catalog", "finance-report-catalog", "/finance", "catalog"),
        workspaceRedirect("project-invoices", "project-invoices", "/invoices", "sales"),
        workspaceRedirect("material-stock", "material-stock", "/warehouse", "stock"),
        {
          path: "workflow-definitions",
          name: "workflow-definitions",
          component: () =>
            import("../../modules/workflow/WorkflowDefinitionsView.vue"),
          meta: {
            permission: "WorkflowDefinitions.Read",
            breadcrumb: ["menu.system", "nav.workflowDefinitions"],
          },
        },
        {
          path: "identity-administration",
          name: "identity-administration",
          component: () =>
            import("../../modules/identity/IdentityAdministrationView.vue"),
          props: { area: "access" },
          // Link cũ `?tab=profiles` sang màn hồ sơ nhân sự, giữ các tham số khác (employeeProfileId…).
          beforeEnter: (to) => {
            if (to.query.tab !== "profiles") return true;
            const { tab: _tab, ...query } = to.query;
            return { path: "/employee-profiles", query };
          },
          meta: {
            permission: [
              "Users.Manage",
              "Users.Read",
              "Roles.Read",
              "Departments.Read",
              "Departments.Manage",
            ],
            permissionMode: "any",
            breadcrumb: ["menu.system", "menu.usersAndAccess"],
          },
        },
        // Trang Tổng quan phân hệ đã bỏ: nội dung nằm ở các góc nhìn của Trung tâm điều hành. Link cũ mở góc nhìn tương ứng;
        // tài khoản không xem được Dashboard thì vào màn chính của phân hệ.
        overviewRedirect("crm", "crm-overview", "crm", "/customers"),
        overviewRedirect("purchasing", "purchasing-overview", "procurement", "/procurement"),
        overviewRedirect("accounting", "accounting-overview", "finance", "/vouchers"),
        overviewRedirect("hr", "hr-overview", "people", "/requests"),
        {
          path: "help",
          name: "help",
          component: () => import("../../modules/help/HelpCenterView.vue"),
          meta: { breadcrumb: ["menu.system", "help.title"] },
        },
        {
          path: "forbidden",
          name: "forbidden",
          component: () => import("../components/ForbiddenView.vue"),
          meta: { breadcrumb: ["common.forbidden"] },
        },
      ],
    },
    { path: "/:pathMatch(.*)*", redirect: "/" },
  ],
});
router.beforeEach((to, from) => {
  const auth = useAuthStore();
  if (!to.meta.public && !auth.authenticated)
    return { name: "login", query: { redirect: to.fullPath } };
  if (to.name === "login" && auth.authenticated)
    return firstAuthorizedMenuPath(auth.can);
  if (to.meta.legal) return true;
  if (auth.authenticated && auth.mustChangePassword && to.name !== "account")
    return { name: "account", hash: "#password", query: { mustChange: "1" } };
  if (!hasRoutePermission(to.meta.permission, to.meta.permissionMode, auth.can)) {
    if (from.name === "login") return firstAuthorizedMenuPath(auth.can);
    return { name: "forbidden" };
  }
});
